/**
 * EntityCard Component
 *
 * Renders clickable cards for teams and users in chat responses.
 * When clicked, triggers callbacks to open the appropriate modal.
 */

import React, { useState } from 'react';

const EntityCard = ({ entity, onTeamClick, onUserClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleClick = () => {
        if (entity.type === 'team' && onTeamClick) {
            onTeamClick(entity.data);
        } else if (entity.type === 'user' && onUserClick) {
            onUserClick(entity.data);
        }
    };

    const isTeam = entity.type === 'team';

    const cardStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        margin: '4px',
        backgroundColor: isTeam ? '#e3f2fd' : '#f3e5f5',
        border: `1px solid ${isTeam ? '#90caf9' : '#ce93d8'}`,
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontSize: '13px',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 2px 8px rgba(0,0,0,0.15)' : 'none'
    };

    const iconStyle = {
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center'
    };

    const nameStyle = {
        fontWeight: 500,
        color: isTeam ? '#1565c0' : '#7b1fa2',
        maxWidth: '180px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    };

    const hintStyle = {
        color: '#666',
        fontSize: '11px',
        marginLeft: '4px'
    };

    // Get display info based on entity type
    const getEntityInfo = () => {
        if (isTeam) {
            const budget = entity.data?.max_budget;
            // spend can be a string (like "$123.45") or number - use spend_raw for calculations
            const spentRaw = entity.data?.spend_raw ?? 0;
            const spentDisplay = entity.data?.spend; // Already formatted string like "$123.45"
            const budgetPercentage = entity.data?.budget_percentage; // Already formatted like "45%"

            if (budget !== undefined && budget !== null) {
                // Use pre-formatted values if available, otherwise format them
                if (spentDisplay && budgetPercentage) {
                    return `${spentDisplay} / $${budget} (${budgetPercentage})`;
                }
                // Fallback: calculate from raw numbers
                const percentage = budget > 0 ? Math.round(spentRaw / budget * 100) : 0;
                return `$${spentRaw.toFixed(2)} / $${budget} (${percentage}%)`;
            }
            return null;
        } else {
            // User card
            const email = entity.data?.user_email || entity.data?.email;
            return email || null;
        }
    };

    const entityInfo = getEntityInfo();

    return (
        <div
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={cardStyle}
            title={`Click to view ${entity.type} details`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleClick();
                }
            }}
        >
            <span style={iconStyle}>
                {isTeam ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={isTeam ? '#1565c0' : '#7b1fa2'}>
                        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                    </svg>
                ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#7b1fa2">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                )}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={nameStyle}>{entity.name}</span>
                {entityInfo && (
                    <span style={{ fontSize: '11px', color: '#666' }}>
                        {entityInfo}
                    </span>
                )}
            </div>
            <span style={hintStyle}>
                View →
            </span>
        </div>
    );
};

export default EntityCard;

